import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Download, Search, Headphones, ExternalLink, Mail, Phone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';

// ---------------------------------------------------------------------------
// Types & constants (mirror the CHECK constraints in the migration)
// ---------------------------------------------------------------------------
type Status = 'new' | 'contacted' | 'scoping' | 'assessment' | 'pilot' | 'won' | 'lost';

interface CxRequest {
  id: string;
  created_at: string;
  updated_at: string;
  company_name: string;
  website: string | null;
  industry: string;
  country: string | null;
  contact_name: string;
  job_title: string | null;
  email: string;
  phone: string | null;
  cs_fte_band: string;
  languages: string[];
  channels: string[];
  current_model: string;
  timeline: string;
  message: string | null;
  consent: boolean;
  language: string;
  source: string;
  page_path: string | null;
  utm: Record<string, string> | null;
  user_agent: string | null;
  status: Status;
  owner_notes: string | null;
  next_action_at: string | null;
}

const STATUSES: Status[] = ['new', 'contacted', 'scoping', 'assessment', 'pilot', 'won', 'lost'];

const STATUS_STYLE: Record<Status, { bg: string; fg: string }> = {
  new:        { bg: '#E8E8FF', fg: '#0504AA' },
  contacted:  { bg: '#FDF3DC', fg: '#8A5A00' },
  scoping:    { bg: '#E3EEF1', fg: '#175E6B' },
  assessment: { bg: '#DDF0E3', fg: '#1E6B3A' },
  pilot:      { bg: '#D6F5E6', fg: '#0F5A38' },
  won:        { bg: '#C6EFCE', fg: '#006100' },
  lost:       { bg: '#F2F2F2', fg: '#666666' },
};

const LABEL: Record<string, Record<string, string>> = {
  industry: { fintech: 'Fintech & payments', trading: 'Trading & brokerage', igaming: 'iGaming', travel: 'Travel & mobility', ecommerce: 'E-commerce', medtech: 'Medtech', saas: 'SaaS', other: 'Other' },
  model: { in_house: 'In-house', outsourced_onshore: 'Outsourced onshore', outsourced_nearshore: 'Outsourced nearshore/offshore', mixed: 'Mixed', unknown: '—' },
  timeline: { immediate: 'Immediate', '1-3_months': '1–3 months', '3-6_months': '3–6 months', exploring: 'Exploring' },
  lang: { de: 'German', en: 'English', ro: 'Romanian', other: 'Other' },
  channel: { voice: 'Voice', email: 'E-mail', chat: 'Chat', backoffice: 'Back-office' },
};

const StatusBadge = ({ status }: { status: Status }) => {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.new;
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: s.bg, color: s.fg }}>
      {status}
    </span>
  );
};

const csvCell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const CxRequestsPage = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Draft state for the detail sheet
  const [draftStatus, setDraftStatus] = useState<Status>('new');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftNext, setDraftNext] = useState('');

  const { data: requests = [], isLoading } = useQuery<CxRequest[]>({
    queryKey: ['cx_assessment_requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cx_assessment_requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as CxRequest[];
    },
  });

  const save = useMutation({
    mutationFn: async ({ id, status, owner_notes, next_action_at }: { id: string; status: Status; owner_notes: string; next_action_at: string | null }) => {
      const { error } = await supabase
        .from('cx_assessment_requests')
        .update({ status, owner_notes: owner_notes || null, next_action_at })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cx_assessment_requests'] });
      toast.success('Request updated');
    },
    onError: (e: Error) => toast.error(e.message || 'Update failed'),
  });

  const quickStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Status }) => {
      const { error } = await supabase.from('cx_assessment_requests').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cx_assessment_requests'] }),
    onError: (e: Error) => toast.error(e.message || 'Update failed'),
  });

  const filtered = useMemo(() => requests.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (industryFilter !== 'all' && r.industry !== industryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${r.company_name} ${r.contact_name} ${r.email} ${r.country ?? ''} ${r.website ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [requests, statusFilter, industryFilter, search]);

  const selected = requests.find((r) => r.id === selectedId) ?? null;

  const openDetail = (r: CxRequest) => {
    setSelectedId(r.id);
    setDraftStatus(r.status);
    setDraftNotes(r.owner_notes ?? '');
    setDraftNext(r.next_action_at ? r.next_action_at.slice(0, 10) : '');
  };

  const counts = STATUSES.reduce<Record<Status, number>>((acc, s) => {
    acc[s] = requests.filter((r) => r.status === s).length;
    return acc;
  }, { new: 0, contacted: 0, scoping: 0, assessment: 0, pilot: 0, won: 0, lost: 0 });

  const exportCSV = () => {
    const header = ['Date', 'Status', 'Company', 'Website', 'Industry', 'Country', 'Contact', 'Job title', 'Email', 'Phone', 'FTE band', 'Languages', 'Channels', 'Current model', 'Timeline', 'Language', 'Next action', 'Notes', 'Message', 'UTM'];
    const rows = filtered.map((r) => [
      r.created_at, r.status, r.company_name, r.website, LABEL.industry[r.industry] ?? r.industry, r.country, r.contact_name, r.job_title, r.email, r.phone,
      r.cs_fte_band, (r.languages || []).join('; '), (r.channels || []).join('; '), LABEL.model[r.current_model] ?? r.current_model, LABEL.timeline[r.timeline] ?? r.timeline,
      r.language, r.next_action_at, r.owner_notes, r.message, Object.entries(r.utm || {}).map(([k, v]) => `${k}=${v}`).join(' '),
    ].map(csvCell).join(','));
    const csv = [header.map(csvCell).join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'cx-assessment-requests.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-fraunces flex items-center gap-2" style={{ color: 'var(--blue)' }}>
            <Headphones className="w-6 h-6" /> CX Requests
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {requests.length} requests · {counts.new} new · {counts.scoping + counts.assessment} in assessment pipeline · {counts.pilot} pilots · {counts.won} won
          </p>
        </div>
        <Button variant="outline" onClick={exportCSV} className="gap-2">
          <Download className="w-4 h-4" /> Export {filtered.length !== requests.length ? `(${filtered.length})` : 'All'}
        </Button>
      </div>

      {/* Pipeline counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
            className="rounded-lg border p-3 text-left transition-colors"
            style={{ background: statusFilter === s ? STATUS_STYLE[s].bg : 'var(--white)', borderColor: statusFilter === s ? STATUS_STYLE[s].fg : 'var(--line-slate)' }}
          >
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: STATUS_STYLE[s].fg }}>{s}</div>
            <div className="text-xl font-bold" style={{ color: 'var(--ink)' }}>{counts[s]}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search company, contact, e-mail, country…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | Status)}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={industryFilter} onValueChange={setIndustryFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All industries</SelectItem>
            {Object.entries(LABEL.industry).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>FTE</TableHead>
              <TableHead>Languages</TableHead>
              <TableHead>Timeline</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Next action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => openDetail(r)}>
                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{format(parseISO(r.created_at), 'MMM dd, yyyy')}</TableCell>
                <TableCell>
                  <div className="font-semibold" style={{ color: 'var(--ink)' }}>{r.company_name}</div>
                  <div className="text-xs text-muted-foreground">{r.country || '—'}</div>
                </TableCell>
                <TableCell>
                  <div style={{ color: 'var(--ink)' }}>{r.contact_name}</div>
                  <div className="text-xs text-muted-foreground">{r.job_title || r.email}</div>
                </TableCell>
                <TableCell><Badge variant="outline">{LABEL.industry[r.industry] ?? r.industry}</Badge></TableCell>
                <TableCell className="text-sm">{r.cs_fte_band}</TableCell>
                <TableCell className="text-sm">{(r.languages || []).map((l) => LABEL.lang[l] ?? l).join(', ') || '—'}</TableCell>
                <TableCell className="text-sm">{LABEL.timeline[r.timeline] ?? r.timeline}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Select value={r.status} onValueChange={(v) => quickStatus.mutate({ id: r.id, status: v as Status })}>
                    <SelectTrigger className="h-8 w-36 border-0 bg-transparent p-0 shadow-none focus:ring-0">
                      <StatusBadge status={r.status} />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {r.next_action_at ? format(parseISO(r.next_action_at), 'MMM dd, yyyy') : '—'}
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground py-12">No requests found</TableCell>
              </TableRow>
            )}
            {isLoading && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground py-12">Loading…</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(open) => { if (!open) setSelectedId(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-fraunces" style={{ color: 'var(--blue)' }}>{selected.company_name}</SheetTitle>
                <SheetDescription>
                  Received {format(parseISO(selected.created_at), 'MMM dd, yyyy HH:mm')} · form language {selected.language.toUpperCase()}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5 text-sm">
                {/* Contact block */}
                <div className="rounded-lg border p-4 space-y-2" style={{ borderColor: 'var(--line-slate)' }}>
                  <div className="font-semibold" style={{ color: 'var(--ink)' }}>{selected.contact_name}{selected.job_title ? ` · ${selected.job_title}` : ''}</div>
                  <div className="flex flex-wrap gap-4 text-muted-foreground">
                    <a className="inline-flex items-center gap-1 hover:underline" href={`mailto:${selected.email}`}><Mail className="w-3.5 h-3.5" /> {selected.email}</a>
                    {selected.phone && <a className="inline-flex items-center gap-1 hover:underline" href={`tel:${selected.phone}`}><Phone className="w-3.5 h-3.5" /> {selected.phone}</a>}
                    {selected.website && <a className="inline-flex items-center gap-1 hover:underline" href={selected.website} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" /> {selected.website.replace(/^https?:\/\//, '')}</a>}
                  </div>
                </div>

                {/* Operation block */}
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {[
                    ['Industry', LABEL.industry[selected.industry] ?? selected.industry],
                    ['Country', selected.country || '—'],
                    ['CS FTE today', selected.cs_fte_band],
                    ['Current model', LABEL.model[selected.current_model] ?? selected.current_model],
                    ['Languages', (selected.languages || []).map((l) => LABEL.lang[l] ?? l).join(', ') || '—'],
                    ['Channels', (selected.channels || []).map((c) => LABEL.channel[c] ?? c).join(', ') || '—'],
                    ['Timeline', LABEL.timeline[selected.timeline] ?? selected.timeline],
                    ['Consent', selected.consent ? 'Yes' : 'No'],
                    ['Page', selected.page_path || '—'],
                    ['UTM', Object.entries(selected.utm || {}).map(([k, v]) => `${k}=${v}`).join(' · ') || '—'],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{k}</dt>
                      <dd style={{ color: 'var(--ink)' }}>{v}</dd>
                    </div>
                  ))}
                </dl>

                {selected.message && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Message</div>
                    <div className="rounded-lg p-3 whitespace-pre-wrap" style={{ background: 'var(--off-white)', color: 'var(--ink)' }}>{selected.message}</div>
                  </div>
                )}

                {/* Pipeline controls */}
                <div className="rounded-lg border p-4 space-y-4" style={{ borderColor: 'var(--line-slate)' }}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Status</div>
                      <Select value={draftStatus} onValueChange={(v) => setDraftStatus(v as Status)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Next action</div>
                      <Input type="date" value={draftNext} onChange={(e) => setDraftNext(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Owner notes</div>
                    <Textarea rows={5} value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} placeholder="Call notes, scope agreed, next step, blockers…" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setSelectedId(null)}>Close</Button>
                    <Button
                      onClick={() => save.mutate({
                        id: selected.id,
                        status: draftStatus,
                        owner_notes: draftNotes,
                        next_action_at: draftNext ? new Date(`${draftNext}T09:00:00`).toISOString() : null,
                      })}
                      disabled={save.isPending}
                    >
                      {save.isPending ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CxRequestsPage;
